import type { LonLat } from '../api/client';

/**
 * Einfache Lageskizze als SVG (ohne Kartenkacheln, daher offline und im Druck immer verfügbar):
 * Route, Nachbarstellen und die eigene Stelle, Norden oben, mit Maßstab.
 */
export function Lageskizze({
  route,
  ziel,
  andere = [],
  radiusM = 250,
  titel = 'Lageskizze',
}: {
  route: LonLat[];
  ziel: LonLat;
  andere?: { position: LonLat; label: string }[];
  radiusM?: number;
  titel?: string;
}) {
  const breite = 300;
  const hoehe = 220;
  const mProGradLat = 111_250;
  const mProGradLon = 111_320 * Math.cos((ziel[1] * Math.PI) / 180);
  const skala = Math.min(breite, hoehe) / 2 / radiusM;
  const xy = ([lon, lat]: LonLat): [number, number] => [
    breite / 2 + (lon - ziel[0]) * mProGradLon * skala,
    hoehe / 2 - (lat - ziel[1]) * mProGradLat * skala,
  ];
  const pfad = route
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${xy(p)[0].toFixed(1)},${xy(p)[1].toFixed(1)}`)
    .join(' ');
  const masstab = 100 * skala;

  return (
    <svg className="lageskizze" viewBox={`0 0 ${breite} ${hoehe}`} role="img" aria-label={titel}>
      <rect x="0" y="0" width={breite} height={hoehe} fill="#fff" stroke="#999" />
      <path
        d={pfad}
        fill="none"
        stroke="#1B1F24"
        strokeWidth="5"
        strokeLinejoin="round"
        strokeLinecap="round"
        opacity="0.35"
      />
      <path d={pfad} fill="none" stroke="#1B1F24" strokeWidth="1.5" strokeDasharray="6 4" />
      {andere.map((a, i) => {
        const [x, y] = xy(a.position);
        if (x < -10 || y < -10 || x > breite + 10 || y > hoehe + 10) return null;
        return (
          <g key={i}>
            <circle cx={x} cy={y} r="7" fill="#fff" stroke="#1B1F24" strokeWidth="1.5" />
            <text x={x} y={y + 3.5} fontSize="9" textAnchor="middle" fontFamily="sans-serif">
              {a.label}
            </text>
          </g>
        );
      })}
      <circle cx={breite / 2} cy={hoehe / 2} r="12" fill="#1B1F24" stroke="#fff" strokeWidth="3" />
      <text
        x={breite / 2}
        y={hoehe / 2 + 4}
        fontSize="11"
        fill="#fff"
        textAnchor="middle"
        fontWeight="bold"
        fontFamily="sans-serif"
      >
        ★
      </text>
      <g transform={`translate(${breite - 22}, 22)`}>
        <path d="M0,-14 L6,6 L0,2 L-6,6 Z" fill="#1B1F24" />
        <text y="18" fontSize="10" textAnchor="middle" fontFamily="sans-serif">
          N
        </text>
      </g>
      <g transform={`translate(10, ${hoehe - 12})`}>
        <line x1="0" y1="0" x2={masstab} y2="0" stroke="#1B1F24" strokeWidth="2" />
        <text x={masstab / 2} y="-4" fontSize="9" textAnchor="middle" fontFamily="sans-serif">
          100 m
        </text>
      </g>
    </svg>
  );
}
